/**
 * Zerbay PDP "Notify me": EmailJS request + Google Sheet backup, and the sold-out size lists.
 *
 * Printed as an ES module by blocks/zerbay-notify-me.liquid. One instance serves every [data-zb-notify] on the page
 * through delegated listeners (so a re-rendered block keeps working). It never imports assets/zerbay-pdp.js: it follows
 * that module through the `zb:pdp-state` event (bubbles, dispatched on the .zb-pdp root; detail { sizeChosen, stock,
 * variantId, colour, size, unit, pending }). A state emitted before this module started is read from `root.zbPdpState`
 * (or rebuilt from the buy-buttons form and the state island). Until a state is known it stays idle.
 * Customer texts come from the block's JSON data script; nothing is written here. No logging.
 */

const EMAILJS_PUBLIC_KEY = 'kgqLf_cfCPGwGt6tk';
const EMAILJS_SERVICE = 'service_osltdto';
const EMAILJS_TEMPLATE = 'template_rww451i';
const BACKUP_URL =
  'https://script.google.com/macros/s/AKfycbwKJbDyvAVRJbA29UREVBuFXR9zEfqVzC1qZRNA8LBIqTK96pTFIsRxa_vdkefymGla/exec';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUCCESS_MS = 5000;
const ALT_SECTION = 'zerbay-size-alternatives';

const ROOT_SELECTOR = '.zb-pdp';
const NOTIFY_SELECTOR = '[data-zb-notify]';

/** root element -> latest zb:pdp-state detail */
const states = new WeakMap();
/** variant id -> section HTML ('' = nothing to show) */
const altCache = new Map();
/** alternatives box -> { shown, loading, controller, failed } */
const altBoxes = new WeakMap();
/** notify block -> success-line timer */
const okTimers = new WeakMap();

let emailjsReady = false;

/* ---------------------------------------------------------------------------------------------- helpers */

function rootOf(el) {
  return el.closest(ROOT_SELECTOR) || el.closest('[data-zb-size-chosen]');
}

function stateFromAttributes(root) {
  const data = root ? root.dataset : {};
  return {
    sizeChosen: data.zbSizeChosen !== 'false', // as zerbay-pdp.js emitState does
    stock: data.zbStock || '',
    variantId: null,
    colour: '',
    size: '',
    unit: data.zbUnit || '',
    pending: false,
  };
}

/** The latest state for a block: the nearest ancestor that received an event, else the root attributes. */
function stateOf(el) {
  for (let node = el; node; node = node.parentElement) {
    if (states.has(node)) return states.get(node);
  }
  return stateFromAttributes(rootOf(el));
}

function readData(block) {
  const node = block.querySelector('script[data-zb-notify-data]');
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    return null;
  }
}

/** State of a root that emitted before this module listened: its stored detail, else rebuilt from the page. */
function initialState(root) {
  if (root.zbPdpState && typeof root.zbPdpState === 'object') return root.zbPdpState;
  if (root.getAttribute('data-zb-size-chosen') !== 'true') return null;
  const idInput = root.querySelector('form[id^="BuyButtons-ProductForm-"] input[name="id"]');
  const id = idInput ? String(idInput.value || '') : '';
  const island = root.querySelector('script[data-zb-pdp-state]');
  if (!id || !island) return null;
  let variant = null;
  try {
    const parsed = JSON.parse(island.textContent);
    variant = parsed && parsed.variants ? parsed.variants[id] : null;
  } catch (error) {
    return null;
  }
  if (!variant) return null;
  return {
    sizeChosen: true,
    stock: root.getAttribute('data-zb-stock') || String(variant.st || ''),
    variantId: id,
    colour: String(variant.c || ''),
    size: String(variant.s || ''),
    unit: root.getAttribute('data-zb-unit') || '',
    pending: false,
  };
}

function initEmailjs() {
  if (emailjsReady) return true;
  const lib = window.emailjs;
  if (!lib || typeof lib.init !== 'function') return false;
  try {
    lib.init(EMAILJS_PUBLIC_KEY);
    emailjsReady = true;
  } catch (error) {
    return false;
  }
  return true;
}

/* ---------------------------------------------------------------------------------------------- info toggle */

function toggleInfo(button) {
  const note = document.getElementById(button.getAttribute('aria-controls') || '');
  if (!note) return;
  const open = button.getAttribute('aria-expanded') !== 'true';
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
  note.hidden = !open;
}

/* ---------------------------------------------------------------------------------------------- send */

function postBackup(inquiry) {
  try {
    const request = window.fetch(BACKUP_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(inquiry),
    });
    if (request && typeof request.catch === 'function') request.catch(() => {});
  } catch (error) {
    /* best effort: never blocks the page */
  }
}

/** The status region is always present and empty; the text is written into it on success and removed after 5 s. */
function showSuccess(block, text) {
  const line = block.querySelector('[data-zb-notify-ok]');
  if (!line || !text) return;
  const mark = document.createElement('span');
  mark.setAttribute('aria-hidden', 'true');
  mark.textContent = '✓ ';
  line.replaceChildren(mark, document.createTextNode(text));
  clearTimeout(okTimers.get(block));
  okTimers.set(
    block,
    setTimeout(() => {
      line.replaceChildren();
    }, SUCCESS_MS)
  );
}

function send(block, button) {
  const data = readData(block);
  const input = block.querySelector('input[name="email"]');
  if (!data || !input || button.disabled) return;

  const texts = data.texts || {};
  const email = input.value.trim();
  if (!email) {
    window.alert(texts.enterEmail);
    input.focus();
    return;
  }
  if (!EMAIL_PATTERN.test(email)) {
    window.alert(texts.invalidEmail);
    input.focus();
    return;
  }

  const state = stateOf(block);
  if (!state.sizeChosen || state.stock !== 'continue_selling' || !state.variantId) return;

  const restore = () => {
    button.disabled = false;
    button.textContent = texts.notifyMe;
  };
  button.disabled = true;
  button.textContent = texts.sending;

  const title = data.productTitle;
  const colour = state.colour || '';
  const size = state.size || '';
  const variantId = String(state.variantId);
  const inquiry = {
    timestamp: new Date().toISOString(),
    email,
    product: title,
    color: colour,
    size,
    variantId,
  };

  // The EmailJS template fields and these English lines are the contract with the shop's inbox (not shown to the shopper).
  const subject = `📦 Stock Request: ${title} - ${colour} ${size}`;
  const message = `Stock Notification Request

Product: ${title}
Color: ${colour}  
Size: ${size}
Variant ID: ${variantId}

Customer Email: ${email}
Timestamp: ${new Date().toLocaleString()}

This customer wants to be notified when this product variant comes back in stock.

Please add them to your stock notification list.`;

  const succeed = () => {
    postBackup(inquiry);
    input.value = '';
    restore();
    showSuccess(block, texts.successMessage);
  };
  const fail = () => {
    postBackup(inquiry);
    window.alert(texts.errorMessage);
    restore();
  };

  if (!initEmailjs()) {
    fail();
    return;
  }
  let sending;
  try {
    sending = window.emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
      to_email: data.shopEmail,
      from_email: email,
      from_name: 'Stock Inquiry Customer',
      subject,
      message,
      product_title: title,
      product_color: colour,
      product_size: size,
      variant_id: variantId,
      shop_name: data.shopName,
      timestamp: new Date().toLocaleString(),
    });
  } catch (error) {
    fail();
    return;
  }
  Promise.resolve(sending).then(succeed, fail);
}

/* ---------------------------------------------------------------------------------------------- size alternatives */

function altRecord(box) {
  let record = altBoxes.get(box);
  if (!record) {
    record = { shown: null, loading: null, controller: null, failed: null };
    altBoxes.set(box, record);
  }
  return record;
}

function clearAlts(box) {
  const record = altRecord(box);
  if (record.controller) record.controller.abort();
  record.controller = null;
  record.loading = null;
  record.shown = null;
  if (box.firstChild) box.replaceChildren();
  box.hidden = true;
  box.setAttribute('aria-busy', 'false');
}

/** Show the heading copy of the active unit (US / EU / UK), else the first copy. */
function applyUnit(box, unit) {
  box.querySelectorAll('.zb-alt__h').forEach((heading) => {
    const copies = Array.from(heading.querySelectorAll('[data-zb-unit]'));
    const match = copies.find((copy) => copy.getAttribute('data-zb-unit') === unit) || copies[0];
    copies.forEach((copy) => {
      copy.hidden = copy !== match;
    });
  });
}

/** The section's content when it holds the lists of this variant, else ''. */
function readSection(text, id) {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const wrap = doc.getElementById(`shopify-section-${ALT_SECTION}`) || doc.body;
  const ok = Array.from(wrap.querySelectorAll('.zb-alt')).some((node) => node.getAttribute('data-zb-alt-variant') === id);
  if (!ok) return '';
  wrap.querySelectorAll('script').forEach((node) => node.remove());
  return wrap.innerHTML.trim();
}

function fillAlts(box, id, html, unit) {
  const record = altRecord(box);
  record.loading = null;
  record.controller = null;
  box.setAttribute('aria-busy', 'false');
  if (!html) {
    clearAlts(box);
    return;
  }
  box.innerHTML = html;
  record.shown = id;
  applyUnit(box, unit);
  box.hidden = false;
}

function refreshAlternatives(block, state) {
  const box = block.querySelector('[data-zb-alternatives]');
  if (!box) return;
  const record = altRecord(box);
  const id = state.sizeChosen && state.stock !== 'in_stock' && state.variantId ? String(state.variantId) : null;

  // a request that failed is not repeated for the same pick; any different pick clears that (so coming back retries)
  if (id !== record.failed) record.failed = null;

  if (!id) {
    clearAlts(box);
    return;
  }
  if (record.failed === id) return;
  if (record.shown === id) {
    applyUnit(box, state.unit);
    return;
  }
  if (record.loading === id) return;

  clearAlts(box);
  const cached = altCache.get(id);
  if (typeof cached === 'string') {
    fillAlts(box, id, cached, state.unit);
    return;
  }

  const data = readData(block);
  if (!data || !data.productUrl) return;
  const controller = new AbortController();
  record.controller = controller;
  record.loading = id;
  box.setAttribute('aria-busy', 'true');

  const url = new URL(data.productUrl, location.origin);
  url.searchParams.set('variant', id);
  url.searchParams.set('section_id', ALT_SECTION);

  const stillWanted = () => {
    const latest = stateOf(block);
    return latest.sizeChosen && latest.stock !== 'in_stock' && String(latest.variantId) === id;
  };

  window
    .fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error('status');
      return response.text();
    })
    .then((text) => {
      const html = readSection(text, id);
      altCache.set(id, html);
      if (record.controller !== controller || controller.signal.aborted) return;
      if (!stillWanted()) {
        record.controller = null;
        record.loading = null;
        box.setAttribute('aria-busy', 'false');
        refreshAlternatives(block, stateOf(block));
        return;
      }
      fillAlts(box, id, html, stateOf(block).unit);
    })
    .catch(() => {
      if (controller.signal.aborted || record.controller !== controller) return;
      clearAlts(box);
      record.failed = id;
    });
}

/* ---------------------------------------------------------------------------------------------- events */

document.addEventListener('zb:pdp-state', (event) => {
  const root = event.target;
  if (!(root instanceof Element) || !event.detail) return;
  states.set(root, event.detail);
  root.querySelectorAll(NOTIFY_SELECTOR).forEach((block) => refreshAlternatives(block, event.detail));
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const toggle = event.target.closest('[data-zb-notify-toggle]');
  if (toggle) {
    toggleInfo(toggle);
    return;
  }
  const sendButton = event.target.closest('[data-zb-notify-send]');
  const block = sendButton ? sendButton.closest(NOTIFY_SELECTOR) : null;
  if (sendButton && block) send(block, sendButton);
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  // back from the page cache: failed requests may be tried again
  document.querySelectorAll(NOTIFY_SELECTOR).forEach((block) => {
    const box = block.querySelector('[data-zb-alternatives]');
    if (box) altRecord(box).failed = null;
    const state = stateOf(block);
    if (state.variantId) refreshAlternatives(block, state);
  });
});

/* ---------------------------------------------------------------------------------------------- init */

try {
  window.localStorage.removeItem('stockInquiries'); // P2-05: older copies of shoppers' e-mails
} catch (error) {
  /* blocked storage */
}

if (!initEmailjs()) window.addEventListener('load', initEmailjs, { once: true });

/* Listeners are attached above; a state emitted before this module started (e.g. a ?variant= arrival opened at init) is then picked up here. */
document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
  const seed = initialState(root);
  if (!seed) return;
  states.set(root, seed);
  root.querySelectorAll(NOTIFY_SELECTOR).forEach((block) => refreshAlternatives(block, seed));
});
